import axios from 'axios';
import moment from 'moment';

const localState = {
  user: {},
  loading: true,
  signedIn: false,
  signInRequired: false,
  timeoutPending: false,
  timeoutId: null,
  timeoutCountdown: 0,
};

const actions = {
  async getUserInfo({ commit, dispatch }) {
    commit('SET_LOADING', true);
    await axios.get('/api/account/user').then((response) => {
      commit('SET_USER', response.data);
      commit('SET_SIGNED_IN', true);
      commit('SET_LOADING', false);
      dispatch('resetTimeout');
    }).catch((error) => {
      // Unauthorized is allowed for anonymous users
      if (error.response && error.response.status !== 401) throw new Error(error.response.data.message);
      commit('SET_LOADING', false);
      commit('SET_SIGNED_IN', false);
    });
  },
  inactivityTimeout({ commit, state }) {
    const signedOut = () => {
      commit('SET_SIGNED_IN', false);
      commit('SET_SIGN_IN_REQUIRED', true);
      if (state.timeoutId) {
        window.clearTimeout(state.timeoutId);
        commit('SET_TIMEOUT_ID', null);
      }
      commit('SET_TIMEOUT_PENDING', false);
    };
    axios.post('/api/signout')
      .then(() => {
        signedOut();
      }).catch((error) => {
        if (error.response && error.response.status === 401) signedOut();
      });
  },
  resetTimeout({ commit, state }) {
    commit('SET_TIMEOUT_PENDING', false);
    if (state.timeoutId) window.clearTimeout(state.timeoutId);
    if (state.signedIn) {
      commit('SET_TIMEOUT_ID', window.setTimeout(
        () => commit('SET_TIMEOUT_PENDING', true),
        moment.duration(14, 'minutes').asMilliseconds(),
      ));
    }
  },
  incrementCountdown({ commit, state }) {
    const newValue = state.timeoutCountdown + 1;
    commit('SET_TIMEOUT_COUNTDOWN', newValue);
  },
  async signIn(context, { data, submittedCallback }) {
    await axios.post('/api/signIn', data)
      .then(() => {
        submittedCallback();
      })
  },
  async create(context, { data, submittedCallback }) {
    await axios.post('/api/account/create', data)
      .then(() => {
        submittedCallback();
      })
  },
  async update({ dispatch }, { data, submittedCallback }) {
    await axios.patch('/api/account/update', data)
      .then(() => {
        dispatch('getUserInfo');
        submittedCallback();
      })
  },
  async sendPasswordReset(_context, { data, submittedCallback }) {
    await axios.post('/api/account/sendPasswordReset', data)
      .then(() => {
        submittedCallback();
      })
  },
  async resetPassword(_context, { data, submittedCallback }) {
    await axios.post('/api/account/resetPassword', data)
      .then(() => {
        submittedCallback();
      })
  },

  async updateNotificationAt({ dispatch }) {
    await axios.patch('/api/account/updateNotificationAt')
      .then(() => {
        dispatch('getUserInfo');
      })
  },
};

const mutations = {
  SET_USER(state, user) {
    state.user = user;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_SIGNED_IN(state, signedIn) {
    state.signedIn = signedIn;
    state.signInRequired = false;
  },
  SET_SIGN_IN_REQUIRED(state, signInRequired) {
    state.signInRequired = signInRequired;
  },
  SET_TIMEOUT_ID(state, timeoutId) {
    state.timeoutId = timeoutId;
  },
  SET_TIMEOUT_PENDING(state, timeoutPending) {
    state.timeoutPending = timeoutPending;
    state.timeoutCountdown = 0;
  },
  SET_TIMEOUT_COUNTDOWN(state, countdown) {
    state.timeoutCountdown = countdown;
  },
};

const getters = {
  publicUser: state => state.user.roles && state.user.roles.length === 0,
  mfsUser: state => state.user.roles && state.user.roles.some(r => r === 'MFS'),
  tceoUser: state => state.user.roles && state.user.roles.some(r => r === 'TCEO'),
  infwbUser: state => state.user.roles && state.user.roles.some(r => r === 'INFWB'),
  mfsAdmin: state => state.user.roles && state.user.roles.some(r => r === 'MFS')
    && state.user.roles.some(r => r === 'Admin'),
  fullName: state => `${state.user.firstName} ${state.user.lastName}`,
  needNotification: state => state.user.needNews,
};

export default {
  actions,
  getters,
  mutations,
  state: localState,
  namespaced: true,
};
